/**
 * PATH: packages/ui/src/components/settings/email-label-prefs/labelPreferencesApi.ts
 * FILE_ID: UI-SETTINGS-LABELPREFS-API-001
 * PURPOSE: Thin I→O wrappers over the Django label-preferences + personal
 *          category endpoints. Each fn takes the SettingsApiClient so the
 *          host wires whichever HTTP client it uses.
 * WHY: Keep the section component free of URL/unwrap plumbing.
 *
 * Endpoints (apiClient baseURL already includes `/api`):
 *   GET    /email/label-preferences/
 *   POST   /email/personal-categories/
 *   PATCH  /email/personal-categories/<id>/
 *   DELETE /email/personal-categories/<id>/
 *   POST   /email/org-categories/        (shared team labels, 2026-07-03)
 */

import type { SettingsApiClient } from '../types'
import type {
  LabelCategory,
  LabelPreferences,
  PersonalCategoryInput,
} from './types'

// Some clients return the axios response ({data}), others the body directly.
const unwrap = (res: any) => (res && typeof res === 'object' && 'data' in res ? res.data : res)

export async function fetchLabelPreferences(
  api: SettingsApiClient,
): Promise<LabelPreferences> {
  const body = unwrap(await api.get('/email/label-preferences/'))
  return { groups: Array.isArray(body?.groups) ? body.groups : [] }
}

export async function createPersonalCategory(
  api: SettingsApiClient,
  input: PersonalCategoryInput,
): Promise<LabelCategory> {
  return unwrap(await api.post('/email/personal-categories/', input))
}

export async function updatePersonalCategory(
  api: SettingsApiClient,
  id: number,
  patch: PersonalCategoryInput,
): Promise<LabelCategory> {
  if (!api.patch) {
    throw new Error('API client does not support PATCH')
  }
  return unwrap(await api.patch(`/email/personal-categories/${id}/`, patch))
}

export async function deletePersonalCategory(
  api: SettingsApiClient,
  id: number,
): Promise<void> {
  await api.delete(`/email/personal-categories/${id}/`)
}

/** Create an ORG-SHARED custom label (visible to the whole team).
 *  Phase 2: may return a proposal envelope (status=proposed) instead of a
 *  category — callers must branch. */
export type OrgCategoryCreateResult =
  | { kind: 'created'; category: LabelCategory }
  | { kind: 'proposed'; proposalId: string; message: string }

export async function createOrgCategory(
  api: SettingsApiClient,
  input: PersonalCategoryInput & { auto_accept?: boolean },
): Promise<OrgCategoryCreateResult> {
  const body = unwrap(await api.post('/email/org-categories/', input))
  if (body?.status === 'proposed' || body?.proposal) {
    return {
      kind: 'proposed',
      proposalId: String(
        body.proposal?.proposal_id || body.proposal?.proposalId || '',
      ),
      message:
        body.message ||
        'Org-shared label proposed for approval — it will appear after accept.',
    }
  }
  return { kind: 'created', category: body as LabelCategory }
}

/** Update an org-shared custom label (creator or admin only, per backend). */
export async function updateOrgCategory(
  api: SettingsApiClient,
  id: number,
  patch: PersonalCategoryInput,
): Promise<LabelCategory> {
  if (!api.patch) {
    throw new Error('API client does not support PATCH')
  }
  return unwrap(await api.patch(`/email/org-categories/${id}/`, patch))
}

/** Delete an org-shared custom label (creator or admin only, per backend). */
export async function deleteOrgCategory(
  api: SettingsApiClient,
  id: number,
): Promise<void> {
  await api.delete(`/email/org-categories/${id}/`)
}
